/**
 * 슬라이드 타입별 생성 프롬프트 관리 (개선된 버전)
 * 
 * 주의: 이 파일의 프롬프트는 CLAUDE.MD에서만 관리됩니다.
 * 직접 수정하지 마세요. CLAUDE.MD의 지침을 따라 수정해주세요.
 */

import { SlideContentItem } from '../types';

export interface SlideTypeConfig {
  objectCountRules: {
    min: number;
    max: number;
    conditions: {
      longContent: { threshold: number; objectCount: number; };
      shortContent: { threshold: number; objectCount: number; };
      withMedia: { objectCount: number; };
    };
  };
  contentRules: {
    maxTextLength?: number;
    mediaTextLimit?: number;
  };
  objectTypes: string[];
  mediaPlacement: 'first' | 'any';
}

export const SLIDE_TYPE_CONFIGS: Record<string, SlideTypeConfig> = {
  ppt: {
    objectCountRules: {
      min: 3,  // PPT는 최소 3개로 강제
      max: 6,
      conditions: {
        longContent: { threshold: 120, objectCount: 3 },
        shortContent: { threshold: 60, objectCount: 6 },
        withMedia: { objectCount: 4 }
      }
    },
    contentRules: {},
    objectTypes: ['list', 'chart', 'image', 'text'],
    mediaPlacement: 'first'
  },
  'card-news': {
    objectCountRules: {
      min: 1,
      max: 2,
      conditions: {
        longContent: { threshold: 50, objectCount: 1 },
        shortContent: { threshold: 50, objectCount: 2 },
        withMedia: { objectCount: 1 }
      }
    },
    contentRules: {
      maxTextLength: 60,
      mediaTextLimit: 50
    },
    objectTypes: ['text', 'chart', 'image'],
    mediaPlacement: 'first'
  },
  'image-card': {
    objectCountRules: {
      min: 1,
      max: 3,
      conditions: {
        longContent: { threshold: 80, objectCount: 2 },
        shortContent: { threshold: 40, objectCount: 3 },
        withMedia: { objectCount: 2 }
      }
    },
    contentRules: {
      maxTextLength: 80,
      mediaTextLimit: 40
    },
    objectTypes: ['image', 'text', 'list'],
    mediaPlacement: 'first'
  }
};

/**
 * 슬라이드 타입별 AI 생성 프롬프트 생성 (완전히 개선된 버전)
 */
export const generateSlideTypePrompt = (
  slideType: string,
  content: string,
  language: 'ko' | 'en',
  slideCount: number
): string => {
  const config = SLIDE_TYPE_CONFIGS[slideType] || SLIDE_TYPE_CONFIGS.ppt;
  const isKorean = language === 'ko';

  // PPT 타입의 경우 특별히 간결하고 명확한 프롬프트
  if (slideType === 'ppt') {
    return generatePPTPrompt(content, slideCount, isKorean);
  }

  // 다른 타입의 프롬프트
  return generateOtherTypePrompt(slideType, content, slideCount, config, isKorean);
};

/**
 * PPT 전용 프롬프트 (매우 명확하고 간결하게)
 */
function generatePPTPrompt(content: string, slideCount: number, isKorean: boolean): string {
  if (isKorean) {
    // 매우 단순하고 명확한 PPT 프롬프트
    return `다음 JSON 배열 형식으로만 응답하세요. 다른 설명 없이 오직 JSON만:

[
${Array.from({ length: slideCount }, (_, i) => {
  const slideNum = i + 1;
  return `  {"slideNumber": ${slideNum}, "category": "title", "format": "short-text", "content": "${content}와 관련된 구체적 제목 ${slideNum}"},
  {"slideNumber": ${slideNum}, "category": "content", "format": "container", "designStyle": "icon-grid", "content": {"layout": "icon-grid", "items": [{"icon": "🎯", "title": "핵심 1", "content": "${content} 관련 핵심 내용 1"}, {"icon": "🚀", "title": "핵심 2", "content": "${content} 관련 핵심 내용 2"}, {"icon": "📊", "title": "핵심 3", "content": "${content} 관련 핵심 내용 3"}]}},
  {"slideNumber": ${slideNum}, "category": "content", "format": "chart", "content": {"type": "bar", "title": "${content} 관련 데이터 ${slideNum}", "data": {"labels": ["항목1", "항목2", "항목3"], "values": [${30 + i * 10}, ${40 + i * 10}, ${35 + i * 5}]}}},
  {"slideNumber": ${slideNum}, "category": "content", "format": "container", "designStyle": "header-list", "content": {"layout": "header-list", "items": [{"icon": "📌", "content": "${content}에 대한 세부 사항 1"}, {"icon": "📌", "content": "${content}에 대한 세부 사항 2"}]}},
  {"slideNumber": ${slideNum}, "category": "content", "format": "container", "designStyle": "column-layout", "content": {"layout": "column-layout", "items": [{"icon": "🎯", "title": "단계 1", "content": "${content} 프로세스의 첫 번째 단계"}, {"icon": "📊", "title": "단계 2", "content": "${content} 프로세스의 두 번째 단계"}, {"icon": "🚀", "title": "단계 3", "content": "${content} 프로세스의 마지막 단계"}]}}`
}).join(',\n  \n')}
]

위 형식을 정확히 따라서 "${content}" 주제에 맞는 내용으로 채워주세요.
각 슬라이드는 다른 관점을 다루고, 제목도 구체적으로 다르게 만드세요.
"개요", "핵심 내용", "결론" 같은 일반적 제목 사용 금지.

차트 생성 규칙:
- format이 "chart"인 경우 content는 반드시 다음 구조를 따라야 합니다:
  {"type": "차트유형", "title": "차트제목", "data": {"labels": [라벨배열], "values": [숫자배열]}}
- type: "bar"(막대), "line"(선), "pie"(파이) 중 하나
- labels와 values 배열의 길이는 동일해야 함
- values는 반드시 숫자 배열이어야 함
- 주제와 관련된 실제 데이터나 의미있는 예시 데이터 사용

Container 생성 규칙:
- format이 "container"인 경우 designStyle과 content를 반드시 포함
- designStyle: "icon-grid"(아이콘 그리드), "header-list"(헤더가 있는 리스트), "card-grid"(카드 그리드), "column-layout"(칼럼 레이아웃) 중 하나
- content.layout: designStyle과 동일한 값
- content.items: 각 항목은 icon(이모지), title(선택적), content(필수) 포함
- icon-grid: 3-6개 항목, 각각 제목과 짧은 설명
- header-list: 2-4개 항목, 제목 없이 아이콘과 내용만
- card-grid: 2-4개 항목, 카드 형태로 표시될 내용
- column-layout: 2-3개 항목, 각 칼럼에 아이콘, 제목(필수), 상세 설명 포함. 주로 프로세스나 단계별 설명에 사용
- 아이콘은 내용과 관련된 적절한 이모지 사용`;
  } else {
    // Very simple and clear PPT prompt
    return `Respond only with this JSON array format. No other text, just JSON:

[
${Array.from({ length: slideCount }, (_, i) => {
  const slideNum = i + 1;
  return `  {"slideNumber": ${slideNum}, "category": "title", "format": "short-text", "content": "Specific title ${slideNum} about ${content}"},
  {"slideNumber": ${slideNum}, "category": "content", "format": "container", "designStyle": "icon-grid", "content": {"layout": "icon-grid", "items": [{"icon": "🎯", "title": "Key 1", "content": "${content} key point 1"}, {"icon": "🚀", "title": "Key 2", "content": "${content} key point 2"}, {"icon": "📊", "title": "Key 3", "content": "${content} key point 3"}]}},
  {"slideNumber": ${slideNum}, "category": "content", "format": "chart", "content": {"type": "bar", "title": "${content} Data ${slideNum}", "data": {"labels": ["Item1", "Item2", "Item3"], "values": [${30 + i * 10}, ${40 + i * 10}, ${35 + i * 5}]}}},
  {"slideNumber": ${slideNum}, "category": "content", "format": "container", "designStyle": "header-list", "content": {"layout": "header-list", "items": [{"icon": "📌", "content": "Detail about ${content} 1"}, {"icon": "📌", "content": "Detail about ${content} 2"}]}},
  {"slideNumber": ${slideNum}, "category": "content", "format": "container", "designStyle": "column-layout", "content": {"layout": "column-layout", "items": [{"icon": "🎯", "title": "Step 1", "content": "First step of ${content} process"}, {"icon": "📊", "title": "Step 2", "content": "Second step of ${content} process"}, {"icon": "🚀", "title": "Step 3", "content": "Final step of ${content} process"}]}}`
}).join(',\n  \n')}
]

Fill in with content about "${content}".
Each slide must cover different aspects with specific, unique titles.
Never use generic titles like "Overview", "Key Points", "Conclusion".

Chart generation rules:
- When format is "chart", content must follow this structure:
  {"type": "chartType", "title": "chartTitle", "data": {"labels": [labelArray], "values": [numberArray]}}
- type: one of "bar", "line", "pie"
- labels and values arrays must have the same length
- values must be an array of numbers
- Use real data or meaningful example data related to the topic

Container generation rules:
- When format is "container", must include designStyle and content
- designStyle: "icon-grid" (icon grid), "header-list" (list with header), "card-grid" (card grid), "column-layout" (column layout)
- content.layout: same value as designStyle
- content.items: each item includes icon(emoji), title(optional), content(required)
- icon-grid: 3-6 items, each with title and short description
- header-list: 2-4 items, icon and content only (no title)
- card-grid: 2-4 items, content to be displayed as cards
- column-layout: 2-3 items, each column with icon, title(required), and detailed description. Best for process or step-by-step explanations
- Use appropriate emojis related to the content`;
  }
}

/**
 * 다른 슬라이드 타입 프롬프트
 */
function generateOtherTypePrompt(
  slideType: string,
  content: string,
  slideCount: number,
  config: SlideTypeConfig,
  isKorean: boolean
): string {
  const prompt = isKorean ? `# ${slideType.toUpperCase()} 슬라이드 생성

## 기본 규칙
1. **정확히 ${slideCount}개 슬라이드**
2. **각 슬라이드: 제목 1개 + 내용 ${config.objectCountRules.min}~${config.objectCountRules.max}개**

## 제목 규칙
- 구체적인 내용 반영 (일반적 표현 금지)
- ${slideType === 'card-news' ? '5-8자' : '3-6자'} 이내

## 내용 규칙
- 객체당 최대 ${config.contentRules.maxTextLength || '제한없음'}자
${config.contentRules.mediaTextLimit ? `- 미디어 포함 시 텍스트 ${config.contentRules.mediaTextLimit}자 이하` : ''}

## JSON 예시
\`\`\`json
[
  {"slideNumber": 1, "category": "title", "format": "short-text", "content": "${slideType === 'card-news' ? '긴급 뉴스' : '핵심 이미지'}", "keywords": ["news", "update"]},
  {"slideNumber": 1, "category": "content", "format": "${config.objectTypes[0]}", "content": "..."},
  {"slideNumber": 1, "category": "content", "format": "chart", "content": {"type": "bar", "title": "데이터 분석", "data": {"labels": ["항목1", "항목2"], "values": [30, 40]}}}
]
\`\`\`

## 처리할 내용
${content}

## 차트 생성 규칙
- format이 "chart"인 경우 content는 반드시: {"type": "차트유형", "title": "제목", "data": {"labels": [라벨배열], "values": [숫자배열]}}
- type: "bar", "line", "pie" 중 하나
- 주제와 관련된 실제 데이터 사용

**JSON 배열로만 응답**` 

: `# ${slideType.toUpperCase()} Slide Generation

## Basic Rules
1. **Exactly ${slideCount} slides**
2. **Each slide: 1 title + ${config.objectCountRules.min}-${config.objectCountRules.max} content**

## Title Rules
- Reflect specific content (no generic terms)
- ${slideType === 'card-news' ? '5-8 chars' : '3-6 chars'} limit

## Content Rules
- Max ${config.contentRules.maxTextLength || 'no limit'} chars per object
${config.contentRules.mediaTextLimit ? `- Text limited to ${config.contentRules.mediaTextLimit} chars with media` : ''}

## JSON Example
\`\`\`json
[
  {"slideNumber": 1, "category": "title", "format": "short-text", "content": "${slideType === 'card-news' ? 'Breaking News' : 'Key Visual'}", "keywords": ["news", "update"]},
  {"slideNumber": 1, "category": "content", "format": "${config.objectTypes[0]}", "content": "..."},
  {"slideNumber": 1, "category": "content", "format": "chart", "content": {"type": "bar", "title": "Data Analysis", "data": {"labels": ["Item1", "Item2"], "values": [30, 40]}}}
]
\`\`\`

## Content to Process
${content}

## Chart Generation Rules
- When format is "chart", content must be: {"type": "chartType", "title": "title", "data": {"labels": [labelArray], "values": [numberArray]}}
- type: one of "bar", "line", "pie"
- Use real data related to the topic

**Respond only with JSON array**`;

  return prompt;
}

/**
 * 슬라이드 타입 설정 가져오기
 */
export const getSlideTypeConfig = (slideType: string): SlideTypeConfig => {
  return SLIDE_TYPE_CONFIGS[slideType] || SLIDE_TYPE_CONFIGS['ppt'];
};

/**
 * 객체 수 계산
 */
export const calculateObjectCount = (
  slideType: string,
  contentLength: number,
  hasMedia: boolean
): { min: number; max: number } => {
  const config = getSlideTypeConfig(slideType);
  
  if (hasMedia) {
    const count = config.objectCountRules.conditions.withMedia.objectCount;
    return { min: Math.max(1, count - 1), max: count };
  }
  
  if (contentLength >= config.objectCountRules.conditions.longContent.threshold) {
    const count = config.objectCountRules.conditions.longContent.objectCount;
    return { min: config.objectCountRules.min, max: count };
  }
  
  if (contentLength <= config.objectCountRules.conditions.shortContent.threshold) {
    const count = config.objectCountRules.conditions.shortContent.objectCount;
    return { min: count - 2, max: count };
  }
  
  return { 
    min: config.objectCountRules.min, 
    max: config.objectCountRules.max 
  };
};

/**
 * 일반적인 제목인지 검증 (확장된 버전)
 */
export const isGenericTitle = (title: string): boolean => {
  const genericPatterns = [
    // 한국어 일반 제목
    /^개요$/i, /^핵심\s*내용$/i, /^주요\s*특징$/i, /^결론$/i, /^소개$/i,
    /^배경$/i, /^목차$/i, /^순서$/i, /^정리$/i, /^요약$/i,
    /^적용\s*사례$/i, /^장점과\s*효과$/i, /^구현\s*방법$/i, /^향후\s*전망$/i,
    /^현황$/i, /^분석$/i, /^전략$/i, /^방안$/i, /^계획$/i,
    // 영어 일반 제목
    /^overview$/i, /^key\s*points?$/i, /^main\s*features?$/i, /^conclusion$/i,
    /^introduction$/i, /^background$/i, /^summary$/i, /^agenda$/i,
    /^case\s*studies?$/i, /^benefits?$/i, /^implementation$/i, /^outlook$/i,
    /^analysis$/i, /^strategy$/i, /^plan$/i, /^approach$/i,
    // 번호가 포함된 제목
    /슬라이드\s*\d+/i, /slide\s*\d+/i, /^제목\s*\d*$/i, /^title\s*\d*$/i,
    // 순서 표시
    /^첫\s*번째$/i, /^두\s*번째$/i, /^세\s*번째$/i, /^마지막$/i, 
    /^first$/i, /^second$/i, /^third$/i, /^last$/i,
    // 단계 표시
    /^단계\s*\d+$/i, /^step\s*\d+$/i, /^phase\s*\d+$/i,
    // 섹션 표시
    /^섹션\s*\d+$/i, /^section\s*\d+$/i, /^part\s*\d+$/i
  ];
  
  // 정확한 매칭 확인
  const exactMatch = genericPatterns.some(pattern => pattern.test(title.trim()));
  
  // 너무 짧은 제목도 일반적으로 간주 (3자 이하)
  const tooShort = title.trim().length <= 3 && !title.match(/[0-9]/);
  
  return exactMatch || tooShort;
};

/**
 * 슬라이드 객체 검증
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export const validateSlideObjects = (
  slides: SlideContentItem[], 
  slideType = 'ppt'
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const config = getSlideTypeConfig(slideType);
  
  // 슬라이드별로 그룹화
  const slideGroups = new Map<number, SlideContentItem[]>();
  slides.forEach(obj => {
    const num = obj.slideNumber;
    if (!slideGroups.has(num)) {
      slideGroups.set(num, []);
    }
    slideGroups.get(num)!.push(obj);
  });
  
  // 각 슬라이드 검증
  slideGroups.forEach((objects, slideNum) => {
    const titles = objects.filter(obj => obj.category === 'title');
    const contents = objects.filter(obj => obj.category === 'content');
    
    // 제목 검증
    if (titles.length === 0) {
      errors.push(`슬라이드 ${slideNum}: 제목 없음`);
    } else if (titles.length > 1) {
      errors.push(`슬라이드 ${slideNum}: 제목이 ${titles.length}개 (1개여야 함)`);
    } else if (isGenericTitle(titles[0].content)) {
      warnings.push(`슬라이드 ${slideNum}: 일반적인 제목 "${titles[0].content}" 사용됨`);
    }
    
    // 내용 검증
    if (contents.length < config.objectCountRules.min) {
      errors.push(`슬라이드 ${slideNum}: 내용 ${contents.length}개 (최소 ${config.objectCountRules.min}개 필요)`);
    } else if (contents.length > config.objectCountRules.max) {
      warnings.push(`슬라이드 ${slideNum}: 내용 ${contents.length}개 (최대 ${config.objectCountRules.max}개 권장)`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * 슬라이드 후처리 (검증 실패 시 자동 수정)
 */
export const postProcessSlides = (
  slides: SlideContentItem[], 
  slideCount: number,
  slideType = 'ppt'
): SlideContentItem[] => {
  const config = getSlideTypeConfig(slideType);
  const processedSlides: SlideContentItem[] = [];
  
  // 슬라이드별로 그룹화
  const slideGroups = new Map<number, SlideContentItem[]>();
  for (let i = 1; i <= slideCount; i++) {
    slideGroups.set(i, []);
  }
  
  slides.forEach(obj => {
    const num = obj.slideNumber;
    if (num >= 1 && num <= slideCount) {
      slideGroups.get(num)!.push(obj);
    }
  });
  
  // 각 슬라이드 처리
  slideGroups.forEach((objects, slideNum) => {
    const titles = objects.filter(obj => obj.category === 'title');
    const contents = objects.filter(obj => obj.category === 'content');
    
    // 제목 처리
    if (titles.length === 0) {
      processedSlides.push({
        slideNumber: slideNum,
        category: 'title',
        format: 'short-text',
        designStyle: 'title-signboard',
        content: `섹션 ${slideNum}`,
        keywords: ['section', 'content', 'slide']
      });
    } else {
      const title = titles[0];
      if (isGenericTitle(title.content)) {
        title.content = `섹션 ${slideNum}: ${title.content}`;
      }
      processedSlides.push(title);
    }
    
    // 내용 처리
    if (contents.length < config.objectCountRules.min) {
      // 기존 내용 추가
      processedSlides.push(...contents);
      
      // 부족한 만큼 기본 객체 추가
      const needed = config.objectCountRules.min - contents.length;
      for (let i = 0; i < needed; i++) {
        processedSlides.push({
          slideNumber: slideNum,
          category: 'content',
          format: 'short-text',
          designStyle: 'info-card',
          content: '추가 내용을 입력해주세요.'
        });
      }
    } else {
      processedSlides.push(...contents.slice(0, config.objectCountRules.max));
    }
  });
  
  return processedSlides;
};

/**
 * 프레젠테이션 제목 생성 프롬프트
 */
export const createPresentationTitlePrompt = (
  content: string,
  language: 'ko' | 'en',
  slideType = 'ppt'
): string => {
  const isKorean = language === 'ko';
  
  return isKorean
    ? `다음 내용의 핵심을 담은 프레젠테이션 제목을 생성하세요:

"${content.substring(0, 200)}..."

규칙:
- 10-20자 이내
- 전체 주제를 포괄하는 구체적 제목
- "프레젠테이션", "발표자료" 같은 일반 단어 금지

제목만 반환:` 
    : `Generate a presentation title capturing the essence of:

"${content.substring(0, 200)}..."

Rules:
- 10-20 characters
- Specific title encompassing the overall theme
- Don't use generic words like "Presentation" or "Slides"

Return only the title:`;
};

/**
 * 컨텐츠 기반 슬라이드 제목 생성
 */
export const createContentBasedTitlePrompt = (
  content: string,
  slideIndex: number,
  language: 'ko' | 'en',
  slideType = 'ppt'
): string => {
  const isKorean = language === 'ko';
  
  return isKorean
    ? `다음 내용의 핵심 주제를 나타내는 구체적인 제목을 생성하세요:

"${content.substring(0, 150)}..."

규칙:
- 5-15자 이내
- 내용의 핵심 키워드 포함
- "개요", "핵심 내용", "주요 특징" 같은 일반적 표현 금지

좋은 예시: "AI 기술의 3가지 혁신", "2025년 시장 전망", "탄소 중립 실천법"

제목만 반환:`
    : `Generate a specific title representing the core topic:

"${content.substring(0, 150)}..."

Rules:
- 5-15 characters
- Include key keywords from content
- Don't use generic terms like "Overview", "Key Points", "Main Features"

Good examples: "3 AI Innovations", "2025 Market Outlook", "Carbon Neutral Methods"

Return only the title:`;
};

/**
 * 슬라이드 제목 검증 및 재생성 프롬프트
 */
export const createSlideTitleValidationPrompt = (
  slideTitle: string,
  slideContent: string,
  language: 'ko' | 'en'
): string => {
  const isKorean = language === 'ko';
  
  // 제목이 유효한지 먼저 확인
  if (!isGenericTitle(slideTitle)) {
    return ''; // 유효한 제목이면 빈 문자열 반환
  }
  
  // 무효한 제목일 경우 재생성 프롬프트
  return isKorean
    ? `현재 제목 "${slideTitle}"은 너무 일반적입니다.

슬라이드 내용: ${slideContent.substring(0, 200)}...

위 내용을 바탕으로 구체적이고 의미있는 제목을 생성하세요.
- 5-15자 이내
- 핵심 키워드 포함
- 실제 내용 반영

제목만 반환:`
    : `Current title "${slideTitle}" is too generic.

Slide content: ${slideContent.substring(0, 200)}...

Generate a specific and meaningful title based on this content.
- 5-15 characters
- Include key keywords
- Reflect actual content

Return only the title:`;
};

/**
 * 슬라이드 수정 프롬프트
 */
export const createSlideUpdatePrompt = (
  currentTitle: string,
  currentContent: string,
  updateRequest: string,
  language: 'ko' | 'en'
): string => {
  const languageInstruction = language === 'ko' 
    ? '한국어로 응답해주세요.' 
    : 'Please respond in English.';

  return `${languageInstruction}

현재 슬라이드:
제목: ${currentTitle}
내용: ${currentContent}

수정 요청: ${updateRequest}

응답 형식:
TITLE: [새로운 제목]
CONTENT: [새로운 내용]`;
};

/**
 * 슬라이드 재생성 프롬프트
 */
export const createSlideRegenerationPrompt = (
  slideTitle: string,
  currentContent: string,
  updateRequest: string,
  language: 'ko' | 'en',
  objectType?: 'text' | 'image' | 'chart'
): string => {
  const isKorean = language === 'ko';
  const typeInstruction = objectType 
    ? (isKorean ? `\n생성할 유형: ${objectType}` : `\nType to generate: ${objectType}`)
    : '';

  return `${isKorean ? '한국어로 응답' : 'Respond in English'}

슬라이드 정보:
제목: ${slideTitle}
현재 내용: ${currentContent}

수정 요청: ${updateRequest}${typeInstruction}

JSON 형식으로 새 객체 생성:
[
  {
    "slideNumber": 1,
    "category": "content",
    "format": "${objectType || 'text'}",
    "content": ${objectType === 'chart' 
      ? '{"type": "bar", "title": "차트 제목", "data": {"labels": ["라벨1", "라벨2"], "values": [값1, 값2]}}' 
      : '"..."'}
  }
]

${objectType === 'chart' ? isKorean ? 
'차트 생성 규칙:\n- type: "bar", "line", "pie" 중 하나\n- 실제 주제와 관련된 데이터 사용\n- labels와 values 배열 길이 동일' : 
'Chart rules:\n- type: one of "bar", "line", "pie"\n- Use real data related to the topic\n- labels and values arrays must have same length' : ''}`;
};

/**
 * AI 컨텐츠 생성 프롬프트
 */
export const generateAIContentPrompt = (
  slideTitle: string,
  elementType: 'text' | 'image' | 'chart',
  language: 'ko' | 'en'
): string => {
  const isKorean = language === 'ko';
  
  switch (elementType) {
    case 'text':
      return isKorean
        ? `"${slideTitle}"에 대한 2-3문장 설명을 작성하세요. 핵심만 간결하게. 텍스트만 반환.`
        : `Write 2-3 sentences about "${slideTitle}". Key points only. Return text only.`;
        
    case 'image':
      return isKorean
        ? `"${slideTitle}" 관련 이미지를 30자 이내로 설명하세요. 설명만 반환.`
        : `Describe an image for "${slideTitle}" in 30 chars. Return description only.`;
        
    case 'chart':
      return isKorean
        ? `"${slideTitle}" 관련 차트 데이터를 JSON 형식으로 생성하세요. 
형식: {"type": "bar", "title": "차트 제목", "data": {"labels": ["라벨1", "라벨2", "라벨3"], "values": [숫자1, 숫자2, 숫자3]}}
type은 bar, line, pie 중 하나. 실제 주제와 관련된 데이터를 사용하세요.`
        : `Generate chart data for "${slideTitle}" in JSON format.
Format: {"type": "bar", "title": "Chart Title", "data": {"labels": ["Label1", "Label2", "Label3"], "values": [number1, number2, number3]}}
Type: one of bar, line, pie. Use real data related to the topic.`;
  }
};

/**
 * 간단한 수정 프롬프트
 */
export const QUICK_PROMPTS = {
  ko: [
    { id: 'concise', label: '더 간결하게', prompt: '핵심만 간결하게 요약' },
    { id: 'detailed', label: '더 구체적으로', prompt: '구체적이고 자세하게 설명' },
    { id: 'paraphrase', label: '다시 표현', prompt: '같은 의미를 다른 표현으로' },
  ],
  en: [
    { id: 'concise', label: 'Make Concise', prompt: 'Summarize key points only' },
    { id: 'detailed', label: 'Make Detailed', prompt: 'Explain in detail' },
    { id: 'paraphrase', label: 'Paraphrase', prompt: 'Rephrase with same meaning' },
  ]
};

/**
 * 슬라이드 생성 품질 모니터링
 */
export const logSlideQuality = (slides: SlideContentItem[], slideType = 'ppt'): void => {
  console.log('=== 슬라이드 생성 품질 분석 ===');
  const validation = validateSlideObjects(slides, slideType);
  
  console.log(`유효성: ${validation.valid ? '✅ 통과' : '❌ 실패'}`);
  
  if (validation.errors.length > 0) {
    console.error('오류:', validation.errors);
  }
  
  if (validation.warnings.length > 0) {
    console.warn('경고:', validation.warnings);
  }
  
  // 슬라이드별 상세 분석
  const slideMap = new Map<number, { title: string; contentCount: number }>();
  slides.forEach(obj => {
    if (!slideMap.has(obj.slideNumber)) {
      slideMap.set(obj.slideNumber, { title: '', contentCount: 0 });
    }
    const slide = slideMap.get(obj.slideNumber)!;
    if (obj.category === 'title') {
      slide.title = obj.content;
    } else {
      slide.contentCount++;
    }
  });
  
  slideMap.forEach((data, num) => {
    const titleStatus = isGenericTitle(data.title) ? '⚠️ 일반적' : '✅ 구체적';
    const contentStatus = data.contentCount >= 3 ? '✅' : '❌';
    console.log(`슬라이드 ${num}: "${data.title}" [${titleStatus}] - 내용 ${data.contentCount}개 [${contentStatus}]`);
  });
};

/**
 * 프로페셔널 슬라이드 생성 프롬프트 (고급 사용자용)
 */
export const PROFESSIONAL_SLIDE_PROMPT = `
전문적인 프레젠테이션을 위한 고급 슬라이드를 생성합니다.

핵심 원칙:
1. 스토리텔링: 문제 → 해결책 → 결과 구조
2. 시각적 임팩트: 차트, 이미지 적극 활용
3. 명확한 메시지: 슬라이드당 하나의 핵심 메시지
4. 행동 유도: 구체적인 다음 단계 제시
`;

/**
 * 슬라이드 타입별 특화 프롬프트
 */
export const SPECIALIZED_PROMPTS = {
  pitchDeck: '투자자를 위한 명확한 비즈니스 가치 전달',
  productLaunch: '제품의 혁신성과 고객 가치 강조',
  training: '학습 효과를 극대화하는 단계별 구성'
};

/**
 * 슬라이드 기본 제목 생성 함수 (긴급 폴백용)
 * 
 * ⚠️ 주의: 이 함수는 AI가 제목을 전혀 생성하지 못한 비상 상황에서만 사용됩니다.
 * 정상적인 경우 AI가 주제에 맞는 구체적인 제목을 생성해야 합니다.
 * 
 * 이 제목들은 슬라이드의 논리적 흐름(기승전결)을 나타내는 것이지
 * 실제 슬라이드 제목이 아닙니다!
 * 
 * 예: "프로젝트 관리" 주제라면
 * ❌ "시작하며" → ✅ "애자일 vs 워터폴의 차이점"
 * ❌ "핵심 가치 제안" → ✅ "반복적 개발의 장점"
 */
export const getDefaultSlideTitle = (
  slideIndex: number, 
  language: 'ko' | 'en',
  slideType = 'ppt',
  totalSlides?: number
): string => {
  const isKorean = language === 'ko';
  
  // 슬라이드 위치에 따른 폴백 제목 (논리적 흐름 표시용)
  const getTitleByPosition = () => {
    // 첫 슬라이드
    if (slideIndex === 0) {
      return isKorean ? '시작하며' : 'Getting Started';
    }
    
    // 마지막 슬라이드
    if (totalSlides && slideIndex === totalSlides - 1) {
      return isKorean ? '마무리하며' : 'Wrapping Up';
    }
    
    // 중간 슬라이드들 - 더 구체적인 제목
    const middleTitles = isKorean 
      ? [
          '현재 상황 분석',
          '핵심 가치 제안', 
          '실행 전략',
          '예상 성과',
          '세부 계획',
          '리스크 관리',
          '성공 요인'
        ]
      : [
          'Current Analysis',
          'Value Proposition',
          'Action Strategy', 
          'Expected Outcomes',
          'Detailed Plans',
          'Risk Management',
          'Success Factors'
        ];
    
    return middleTitles[slideIndex % middleTitles.length] || (isKorean ? '섹션' : 'Section');
  };
  
  return getTitleByPosition() || (isKorean ? '슬라이드' : 'Slide');
};